import { Pub, Sub, State } from '../../../../../../../../../shared/state/state.abstract.ts';
import { Observable, merge, tap, BehaviorSubject, share, switchMap, map, filter } from 'rxjs';
import {
  ListArticleNumberDuplicatesFromCaQueryRes,
  configurationService,
  ExecuteArticlesImportFromCaMutationRes,
  SavePriceCategoriesMappingBetweenWaAndCaMutationRes,
} from '../../../../../../services/configuration.service.ts';
import { responseHandler } from '../../../../../../../../../shared/responseHandler/responseHandler.ts';
import {
  Wa_PriceCategoryMappingBetweenWaAndCaInput,
  C_Save_Operation_Status,
} from '../../../../../../../../../graphql/generatedModel.ts';
import { stepperState } from '../../../../../../../../../shared/components/stepper/states/stepper.state.ts';

class PubImpl extends Pub<IReimportArticlesFromCA> {
  init(state: Partial<IReimportArticlesFromCA>) {
    this.emit('init', { ...state });
  }
  recheckDuplicatesFromCA() {
    this.emit('recheckDuplicatesFromCA', {});
  }
  saveData(dataForSave: IReimportArticlesFromCA['dataForSave']) {
    this.emit('saveData', { dataForSave });
  }
  clearState() {
    this.emit('clearState', initReimportArticlesFromCAStateData);
  }
}

class SubImpl extends Sub<IReimportArticlesFromCA> {
  private firstPageLoading$ = new BehaviorSubject<boolean>(true);
  private shareFirstPageLoading$: Observable<boolean> = this.firstPageLoading$.pipe(share());
  private secondPageLoading$ = new BehaviorSubject<boolean>(false);
  private shareSecondPageLoading$: Observable<boolean> = this.secondPageLoading$.pipe(share());

  protected actionHandlers(): Observable<IReimportArticlesFromCA> {
    return merge(this.updateState(), this.recheckDuplicatesFromCA(), this.saveData()).pipe(
      tap((state: IReimportArticlesFromCA) => {
        this.stream$.next({ ...state, action: 'internalUpdate' });
      }),
    );
  }

  firstPageLoading(): Observable<boolean> {
    return this.shareFirstPageLoading$;
  }
  secondPageLoading(): Observable<boolean> {
    return this.shareSecondPageLoading$;
  }

  private recheckDuplicatesFromCA() {
    return this.actionListener('recheckDuplicatesFromCA').pipe(
      tap(() => configurationService.pub.listArticleNumberDuplicatesFromCA()),
      switchMap(() => {
        return configurationService.sub
          .listArticleNumberDuplicatesFromCA()
          .pipe(
            responseHandler<ListArticleNumberDuplicatesFromCaQueryRes>({ errorReturnType: [] }),
          );
      }),
      map((listArticleNumberDuplicatesFromCA) => {
        const updatedState = this.stream$.getValue();
        return { ...updatedState, listArticleNumberDuplicatesFromCA };
      }),
    );
  }

  private saveData() {
    return this.actionListener('saveData').pipe(
      tap(({ dataForSave }) => {
        configurationService.pub.savePriceCategoriesMappingBetweenWAAndCA({ mapping: dataForSave });
        this.secondPageLoading$.next(true);
      }),
      switchMap(() => {
        return configurationService.sub.savePriceCategoriesMappingBetweenWAAndCA().pipe(
          responseHandler<SavePriceCategoriesMappingBetweenWaAndCaMutationRes>({
            errorReturnType: {
              status: C_Save_Operation_Status.SOS0_DATA_UNCHANGED,
            },
            customErrorHandler: () => {
              this.secondPageLoading$.next(false);
              return 'errors.unknown';
            },
          }),
          filter(({ status }) => status === C_Save_Operation_Status.SOS1_DATA_CHANGED),
          tap(() => configurationService.pub.executeArticlesImportFromCA()),
          switchMap(() => {
            return configurationService.sub.executeArticlesImportFromCA().pipe(
              responseHandler<ExecuteArticlesImportFromCaMutationRes>({
                errorReturnType: {
                  recordsInserted: null,
                  recordsUpdated: null,
                },
                customErrorHandler: () => {
                  this.secondPageLoading$.next(false);
                  return 'errors.unknown';
                },
              }),
            );
          }),
          map((data) => {
            stepperState.pub.nextStep({ activeStep: 1, valid: true });
            this.secondPageLoading$.next(false);
            const updatedState = this.stream$.getValue();
            return {
              ...updatedState,
              reimportResult: {
                recordsInserted: data.recordsInserted,
                recordsUpdated: data.recordsUpdated,
              },
            };
          }),
        );
      }),
    );
  }

  private updateState() {
    return this.actionListener(['updateState', 'init']);
  }
}

class ReimportArticlesFromCAState extends State<IReimportArticlesFromCA> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

const initReimportArticlesFromCAStateData: IReimportArticlesFromCA = {
  action: undefined,
  listArticleNumberDuplicatesFromCA: [],
  dataForSave: [],
  reimportResult: {},
};

export const reimportArticlesFromCAState = new ReimportArticlesFromCAState(
  initReimportArticlesFromCAStateData,
);

export interface IReimportArticlesFromCA {
  action:
    | undefined
    | 'internalUpdate'
    | 'checkDuplicatesFromCA'
    | 'recheckDuplicatesFromCA'
    | 'updateState'
    | 'clearState'
    | 'saveData'
    | 'init';
  listArticleNumberDuplicatesFromCA: ListArticleNumberDuplicatesFromCaQueryRes;
  dataForSave: Array<Wa_PriceCategoryMappingBetweenWaAndCaInput>;
  reimportResult: {
    recordsInserted?: ExecuteArticlesImportFromCaMutationRes['recordsInserted'];
    recordsUpdated?: ExecuteArticlesImportFromCaMutationRes['recordsUpdated'];
  };
}
